/**
 * /checkin/checkout/return — Square-hosted checkout return page.
 *
 * Square redirects here with `?session_id=<uuid>`. We poll the backend
 * status endpoint every 2 seconds until an entitlement lands (or a
 * short timeout falls back to a "check your email" state — access is
 * webhook-driven and may take a moment).
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Mail } from "lucide-react";
import { CheckInShell, CheckInLogo, PrimaryButton, GhostButton, CHECKIN_PALETTE } from "./_shell";
import { checkinApi, getToken } from "@/lib/checkinApi";

const POLL_MS = 2000;
const MAX_ATTEMPTS = 20;   // ~40 seconds

export default function CheckInCheckoutReturn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState({ status: "polling", balance: null, kinds: [] });
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!getToken()) {
      navigate("/checkin/signin", { replace: true });
      return;
    }
    if (!sessionId) {
      setState({ status: "missing_session", balance: 0, kinds: [] });
      return;
    }
    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const t = getToken();
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/checkin/checkout/status/${encodeURIComponent(sessionId)}`,
          { headers: { Authorization: `Bearer ${t}` } },
        );
        const j = await r.json();
        if (!r.ok) throw new Error(j?.detail?.message || "Status check failed");
        if (j.status === "active") {
          setState({ status: "active", balance: j.balance || 0, kinds: j.entitlement_kinds || [] });
          if (timerRef.current) clearInterval(timerRef.current);
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "email_fallback", balance: 0, kinds: [] });
          if (timerRef.current) clearInterval(timerRef.current);
        }
      } catch {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "email_fallback", balance: 0, kinds: [] });
          if (timerRef.current) clearInterval(timerRef.current);
        }
      }
    };
    // Fire immediately, then poll.
    poll();
    timerRef.current = setInterval(poll, POLL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [sessionId, navigate]);

  return (
    <CheckInShell testid="checkin-checkout-return">
      <div className="pt-2 flex items-center justify-center">
        <CheckInLogo />
      </div>

      <div className="mt-16 text-center">
        {state.status === "polling" && (
          <div data-testid="return-polling">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: CHECKIN_PALETTE.purple }} />
            <h1 className="mt-6 font-serif text-3xl leading-tight"
                style={{ color: CHECKIN_PALETTE.ink, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              Getting your space ready…
            </h1>
            <p className="mt-3 text-[13.5px]" style={{ color: "#5C5468" }}>
              We&rsquo;re confirming your payment with Square. This usually takes a few seconds.
            </p>
          </div>
        )}
        {state.status === "active" && (
          <div data-testid="return-active">
            <CheckCircle2 className="w-10 h-10 mx-auto"
                            style={{ color: CHECKIN_PALETTE.green }} />
            <h1 className="mt-6 font-serif text-3xl leading-tight"
                style={{ color: CHECKIN_PALETTE.ink, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              You&rsquo;re all set.
            </h1>
            <p className="mt-3 text-[13.5px]" style={{ color: "#5C5468" }}>
              You have <strong data-testid="return-balance">{state.balance}</strong> Talk Credits
              waiting. Dr. C is ready when you are.
            </p>
          </div>
        )}
        {state.status === "email_fallback" && (
          <div data-testid="return-email">
            <Mail className="w-8 h-8 mx-auto" style={{ color: CHECKIN_PALETTE.purple }} />
            <h1 className="mt-6 font-serif text-3xl leading-tight"
                style={{ color: CHECKIN_PALETTE.ink, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              Your receipt is on its way.
            </h1>
            <p className="mt-3 text-[13.5px]" style={{ color: "#5C5468" }}>
              Payment is confirmed. Provisioning can take a moment on Square&rsquo;s side —
              refresh your check-in in a bit and your credits will be there.
            </p>
          </div>
        )}
        {state.status === "missing_session" && (
          <div data-testid="return-missing">
            <h1 className="font-serif text-3xl leading-tight"
                style={{ color: CHECKIN_PALETTE.ink, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              Something got lost in the shuffle.
            </h1>
            <p className="mt-3 text-[13.5px]" style={{ color: "#5C5468" }}>
              We couldn&rsquo;t find your checkout session. Head back home and try again.
            </p>
          </div>
        )}
      </div>

      <div className="mt-auto pt-10 space-y-3">
        {state.status === "active" ? (
          <Link to="/checkin/conversation">
            <PrimaryButton testid="return-cta-conversation">Start today&rsquo;s check-in</PrimaryButton>
          </Link>
        ) : (
          <Link to="/checkin/home">
            <PrimaryButton testid="return-cta-home">Back to home</PrimaryButton>
          </Link>
        )}
        <Link to="/checkin/plans">
          <GhostButton testid="return-cta-plans">See plans</GhostButton>
        </Link>
      </div>
    </CheckInShell>
  );
}
